import { useCallback, useState } from "react"

import { toast } from "@coval/ui"

import { logger } from "@/lib/logger"
import type { IVRFlow } from "@/types/IVRFlow"

import { isIVRFlow, validateIVRFlow } from "./ivr-flow-utils"

const log = logger("IVRTreeBuilder")

// Shortcut so users get the canned example without burning an LLM call.
// Matched after lowercasing + trimming whitespace.
const EXAMPLE_TRIGGER_PROMPTS: ReadonlySet<string> = new Set([
  "create example tree",
  "example tree",
  "load example",
  "example",
])

/** Whether a prompt is the canned "load the example tree" shortcut. */
export const isExampleTriggerPrompt = (prompt: string): boolean =>
  EXAMPLE_TRIGGER_PROMPTS.has(prompt.trim().toLowerCase())

interface UseIVRGenerationArgs {
  /** Owner-provided generator. When absent, generation is disabled. */
  generateIvrFlow?: (prompt: string) => Promise<IVRFlow>
  /** Invoked when the prompt matches the example shortcut. */
  onLoadExample: () => void
  /** Invoked with a successfully generated + parsed flow. */
  onGenerated: (flow: IVRFlow) => void
}

interface IVRGenerationApi {
  generatePrompt: string
  setGeneratePrompt: (value: string) => void
  isGenerating: boolean
  handleGenerate: () => Promise<void>
}

/**
 * Owns the "Generate from a prompt" tab: prompt text, in-flight state, and the
 * generate handler. The example shortcut short-circuits to `onLoadExample`
 * without an LLM call; a successful generation is handed to `onGenerated`.
 * Toasts for the success/with-issues/failure outcomes are raised here to keep
 * the call site declarative.
 */
export const useIVRGeneration = ({
  generateIvrFlow,
  onLoadExample,
  onGenerated,
}: UseIVRGenerationArgs): IVRGenerationApi => {
  const [generatePrompt, setGeneratePrompt] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)

  const handleGenerate = useCallback(async () => {
    const trimmed = generatePrompt.trim()
    if (!trimmed) {
      return
    }
    if (isExampleTriggerPrompt(trimmed)) {
      onLoadExample()
      setGeneratePrompt("")
      return
    }
    if (!generateIvrFlow) {
      toast.danger({
        title: "Generation unavailable",
        description: "This IVR builder was not configured with a generator.",
      })
      return
    }
    setIsGenerating(true)
    try {
      const result = await generateIvrFlow(trimmed)
      if (!isIVRFlow(result)) {
        toast.danger({
          title: "Could not parse generated flow",
          description:
            "The AI response did not match the expected IVR flow shape.",
        })
        return
      }
      onGenerated(result)
      setGeneratePrompt("")
      const validationResult = validateIVRFlow(result)
      if (validationResult.valid) {
        toast.success({ title: "IVR flow generated" })
      } else {
        toast.warning({
          title: "Generated with issues",
          description:
            "Review the highlighted issues — you may need to tweak a few nodes.",
        })
      }
    } catch (error) {
      log.error("Failed to generate IVR flow", error)
      toast.error({
        title: "Generation failed",
        error,
        fallback: "Could not generate IVR flow. Please try again.",
      })
    } finally {
      setIsGenerating(false)
    }
  }, [generatePrompt, generateIvrFlow, onLoadExample, onGenerated])

  return {
    generatePrompt,
    setGeneratePrompt,
    isGenerating,
    handleGenerate,
  }
}
